import { useState } from 'react';
import { X, Search, Plus, Minus, ChevronLeft, CheckCircle } from 'lucide-react';
import { useSearchCards, useAddToCollection } from '@/hooks/useApi';
import CardImage from '@/components/CardImage';
import toast from 'react-hot-toast';

const CONDITIONS = ['Mint', 'Near Mint', 'Excellent', 'Good', 'Played', 'Poor'] as const;

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
}

export default function AddCardModal({ onClose, onSuccess }: Props) {
  const [step, setStep]           = useState<'search' | 'details' | 'done'>('search');
  const [query, setQuery]         = useState('');
  const [selectedCard, setSelectedCard] = useState<any | null>(null);
  const [condition, setCondition] = useState<string>('Near Mint');
  const [quantity, setQuantity]   = useState(1);
  const [notes, setNotes]         = useState('');
  const [isForTrade, setIsForTrade] = useState(false);

  const { data: results = [], isFetching } = useSearchCards(query);
  const { mutate: addCard, isLoading }     = useAddToCollection();

  const handleSelectCard = (card: any) => {
    setSelectedCard(card);
    setStep('details');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCard) return;

    addCard(
      {
        card_id:      selectedCard.id,
        condition,
        quantity,
        notes:        notes.trim() || null,
        is_for_trade: isForTrade,
      },
      {
        onSuccess: () => {
          setStep('done');
          toast.success(`${selectedCard.name} added to your collection!`);
          setTimeout(() => {
            onSuccess?.();
            onClose();
          }, 1200);
        },
        onError: () => { toast.error('Failed to add card'); },
      }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-zinc-800 flex-shrink-0">
          <div className="flex items-center gap-2">
            {step === 'details' && (
              <button
                onClick={() => { setStep('search'); setSelectedCard(null); }}
                className="p-1 rounded-lg text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800 transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <h2 className="font-bold text-lg text-gray-900 dark:text-zinc-100">
              {step === 'search'  ? 'Add Card to Collection' :
               step === 'details' ? 'Card Details' : 'Added!'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ── Step: Search ── */}
        {step === 'search' && (
          <>
            <div className="px-5 pt-4 pb-3 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-zinc-500" />
                <input
                  type="text"
                  autoFocus
                  placeholder="Search by card name…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 dark:border-zinc-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
              </div>
            </div>

            <div className="overflow-y-auto flex-1 px-5 pb-5">
              {query.length <= 1 ? (
                <div className="py-12 text-center text-gray-500 dark:text-zinc-500 text-sm">
                  Type at least 2 characters to search
                </div>
              ) : isFetching ? (
                <div className="py-12 flex justify-center">
                  <div className="animate-spin h-6 w-6 rounded-full border-2 border-brand-600 border-t-transparent" />
                </div>
              ) : results.length === 0 ? (
                <div className="py-12 text-center text-gray-500 dark:text-zinc-500 text-sm">
                  No cards found for "{query}"
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {results.map((card: any) => (
                    <button
                      key={card.id}
                      onClick={() => handleSelectCard(card)}
                      className="group text-left focus:outline-none"
                    >
                      <div className="aspect-[3/4] rounded-xl overflow-hidden border-2 border-transparent group-hover:border-brand-400 group-focus:border-brand-500 transition-colors">
                        <CardImage
                          src={undefined}
                          alt={card.name}
                          cardName={card.name}
                          cardNumber={card.card_number}
                          game={card.game}
                          className="w-full h-full group-hover:scale-105 transition-transform duration-200"
                        />
                      </div>
                      <p className="mt-1 text-xs font-medium text-gray-800 dark:text-zinc-200 truncate leading-tight">
                        {card.name}
                      </p>
                      {card.set_name && (
                        <p className="text-xs text-gray-500 dark:text-zinc-500 truncate leading-tight">{card.set_name}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Step: Details ── */}
        {step === 'details' && selectedCard && (
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            {/* Selected card preview */}
            <div className="flex items-center gap-3 px-5 py-3 bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800 flex-shrink-0">
              <div className="h-14 w-10 rounded-lg overflow-hidden bg-gray-200 dark:bg-zinc-700 flex-shrink-0">
                {selectedCard.image_url ? (
                  <img src={selectedCard.image_url} alt={selectedCard.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xl">🃏</div>
                )}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 dark:text-zinc-100 truncate">{selectedCard.name}</p>
                {selectedCard.set_name && (
                  <p className="text-xs text-gray-500 dark:text-zinc-500 truncate">
                    {selectedCard.game && <span>{selectedCard.game} · </span>}
                    {selectedCard.set_name}
                  </p>
                )}
              </div>
            </div>

            <div className="overflow-y-auto flex-1 px-5 py-5 space-y-5">
              {/* Condition */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Condition *</label>
                <div className="grid grid-cols-3 gap-2">
                  {CONDITIONS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCondition(c)}
                      className={`py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                        condition === c
                          ? 'bg-brand-600 text-white border-brand-600'
                          : 'bg-white dark:bg-zinc-900 text-gray-700 dark:text-zinc-300 border-gray-200 dark:border-zinc-800 hover:border-brand-400'
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">Quantity</label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    className="p-2 rounded-lg border border-gray-200 dark:border-zinc-800 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 disabled:opacity-40 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(9999, Number(e.target.value))))}
                    min={1}
                    max={9999}
                    className="w-20 text-center border border-gray-200 dark:border-zinc-800 rounded-lg py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.min(9999, quantity + 1))}
                    className="p-2 rounded-lg border border-gray-200 dark:border-zinc-800 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-500 dark:text-zinc-500">copies</span>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-zinc-300 mb-2">
                  Notes <span className="font-normal text-gray-500 dark:text-zinc-500">(optional)</span>
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Foil, 1st Edition, Signed…"
                  maxLength={200}
                  className="w-full border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
              </div>

              {/* For trade */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isForTrade}
                  onChange={(e) => setIsForTrade(e.target.checked)}
                  className="accent-brand-600 h-4 w-4"
                />
                <span className="text-sm text-gray-700 dark:text-zinc-300">Mark as available for trade</span>
              </label>
            </div>

            <div className="px-5 py-4 border-t border-gray-200 dark:border-zinc-800 flex gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => { setStep('search'); setSelectedCard(null); }}
                className="flex-1 py-2.5 border border-gray-200 dark:border-zinc-800 rounded-xl text-sm font-medium text-gray-700 dark:text-zinc-300 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900 transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {isLoading ? 'Adding…' : 'Add to Collection'}
              </button>
            </div>
          </form>
        )}

        {/* ── Step: Done ── */}
        {step === 'done' && (
          <div className="py-14 flex flex-col items-center text-center px-6">
            <CheckCircle className="h-16 w-16 text-emerald-500 mb-3" />
            <p className="font-semibold text-gray-800 dark:text-zinc-200 text-lg">Added to Collection!</p>
            <p className="text-gray-500 dark:text-zinc-500 text-sm mt-1">
              {quantity}× <strong>{selectedCard?.name}</strong> ({condition})
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
